// Title-case a finished string (used as a save-time safety pass).
export const toPascalCase = (str) => {
  if (!str || typeof str !== 'string') return '';
  return str.trim().split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
};

// Live-typing variant: preserves a single trailing space so the user can keep typing.
export const normaliseText = (val) => {
  if (!val) return '';
  const trailingSpace = val.endsWith(' ') ? ' ' : '';
  const words = val.trimStart().split(/\s+/).filter(Boolean);
  return words.map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ') + trailingSpace;
};

// Field limits, shared by every guest form so the printed card and the
// compact guest rows always fit.
export const MAX_NAME = 15;
// A name is title-cased AND cut to 15 characters as it is typed.
export const nameInput = (val) => normaliseText(val).slice(0, MAX_NAME);
// Exactly 10 digits: a pasted "+91 98222 10195" or "091-98222-10195" keeps its
// last 10, so the country code and the spaces never reach the database.
export const tenDigits = (val) => {
  let d = String(val == null ? '' : val).replace(/\D/g, '');
  if (d.length > 10) d = d.slice(-10);            // drop 91 / 0 / 0091 prefixes
  return d.slice(0, 10);
};

export const SIDES = ['Common', 'Groom', 'Bride'];
export const EVENT_TYPES = [
  'Birthday Party', 'Marriage', 'Engagement', 'Anniversary', 'Corporate Event',
  'Thread Ceremony', 'Cocktail Party', 'Reception', 'Naming Ceremony',
  'Housewarming', 'Baby Shower', 'Other',
];
// Sub-events ("functions") that make up one event.
export const FUNCTION_TYPES = [
  'Opening / Welcome', 'Main Celebration', 'Dinner', 'Reception', 'Cocktail Party',
  'Cake Cutting', 'Games', 'Return Gifts', 'Haldi', 'Mehndi', 'Sangeet',
  'Wedding', 'Wedding Cum Reception', 'Ring Ceremony', 'Conference', 'Awards', 'Other',
];
export const isMarriageEventType = (value) => {
  const type = String(value || '').trim().toLowerCase();
  return type === 'marriage' || type === 'wedding';
};
// Member invite lifecycle (the "mark" statuses).
// Function Type "Other" → the user types a new name (saved as a new type).
export const OTHER_FUNCTION_TYPE = 'Other';

// FunctionTime is stored as 24-h "HH:MM" (from <input type="time">). Older rows
// hold free text like "7:00 PM" — shown as-is. fmtTime → "7:00 PM" for display.
// "7:00 PM" / "19:00:00" / "19:00" → "19:00" so any stored value binds to the clock input.
export const toTimeInput = (v) => {
  const s = String(v || '').trim();
  const m = /^(\d{1,2}):(\d{2})(?::\d{2})?\s*([AaPp][Mm])?$/.exec(s);
  if (!m) return '';
  let h = Number(m[1]);
  if (m[3]) { const pm = /p/i.test(m[3]); if (pm && h < 12) h += 12; if (!pm && h === 12) h = 0; }
  if (h > 23) return '';
  return `${String(h).padStart(2, '0')}:${m[2]}`;
};
// Display: any stored value → "7:00 PM"; free text that is not a time is shown as-is.
export const fmtTime = (v) => {
  const t = toTimeInput(v);
  if (!t) return v || '';
  const [h, mm] = t.split(':').map(Number);
  return `${h % 12 === 0 ? 12 : h % 12}:${String(mm).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};

export const INVITE_STATUSES = ['Pending', 'Invited', 'Confirmed', 'Declined'];

// Local calendar date as YYYY-MM-DD (not UTC — the app runs in IST).
export const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
// A function whose date is before today is "over": guests can no longer be
// invited to it (it stays visible in filters / headcount for the record).
export const isPastFunction = (fn) => {
  const d = String(fn?.FunctionDate || '').slice(0, 10);
  return !!d && d < todayISO();
};
// Who can see a guest a member added: Common = all members, Private = only the adder.
export const VISIBILITIES = ['Private', 'Common'];
export const visibilityChipClass = (v) => (v === 'Common' ? 'chip ok' : 'chip pend');
export const inviteChipClass = (s) =>
  s === 'Confirmed' ? 'chip ok' : s === 'Declined' ? 'chip danger'
    : s === 'Invited' ? 'chip warn' : 'chip pend';

export const sideChipClass = (side) =>
  side === 'Groom' || side === 'Male' ? 'chip male' : side === 'Bride' || side === 'Female' ? 'chip female' : 'chip common';

// Full display name from Surname + (first) Name + Father/Husband parts.
// `nameKey` is the first-name column ('GuestName' for guests, 'MemberName' for party).
export const fullName = (row, nameKey = 'GuestName') =>
  [row.Surname, row[nameKey], row.FatherHusband].map(x => (x || '').trim()).filter(Boolean).join(' ')
  || (row[nameKey] || '').trim() || '—';

// ── Date display / form helpers (API gives 'YYYY-MM-DD' / 'YYYY-MM-DD HH:MM:SS') ─
export const fmtDate = (v) => {
  if (!v) return '—';
  const d = String(v).slice(0, 10).split('-');
  return d.length === 3 ? `${d[2]}-${d[1]}-${d[0]}` : String(v);
};
export const fmtDateTime = (v) => {
  if (!v) return '—';
  const s = String(v);
  const datePart = fmtDate(s);
  const time = s.length >= 16 ? s.slice(11, 16) : '';
  return time ? `${datePart} ${time}` : datePart;
};
// 'YYYY-MM-DD HH:MM:SS' → 'YYYY-MM-DDTHH:MM' for <input type=datetime-local>
export const toDatetimeLocal = (v) => (v ? String(v).replace(' ', 'T').slice(0, 16) : '');
// 'YYYY-MM-DDTHH:MM' → 'YYYY-MM-DD HH:MM:00' for MySQL
export const fromDatetimeLocal = (v) => {
  if (!v) return null;
  const s = String(v).replace('T', ' ');
  return s.length === 16 ? `${s}:00` : s;
};
export const money = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
